import { forwardRef, type ReactNode } from 'react';

import { DataTestId } from '../../core/types/data-props.js';
import { StylingProps } from '../../core/types/styling-props.js';
import { canReceiveDefaultTextFormat as _canReceiveDefaultTextFormat } from '../../core/utils/can-receive-default-text-format.js';
import { Flex } from '../../layouts/flex/Flex.js';
import { Text } from '../../typography/text/Text.js';

//#region Typings
/**
 * Accepted properties for the MessageContainer Title.
 * @public
 */
export interface MessageContainerTitleProps extends StylingProps, DataTestId {
  /** Elements to be displayed in the title slot. */
  children: ReactNode;
}
//#endregion

/**
 * The MessageContainer Title component.
 * @public
 */
export const Title = forwardRef<HTMLDivElement, MessageContainerTitleProps>(
  (props, forwardedRef) => {
    const {
      children,
      className: consumerClassName,
      style: consumerStyle,
      'data-testid': dataTestId,
      ...remainingProps
    }: MessageContainerTitleProps = props;

    const childIsText = _canReceiveDefaultTextFormat(children);

    return (
      <Flex
        data-testid={dataTestId}
        ref={forwardedRef}
        className={consumerClassName}
        style={consumerStyle}
        {...remainingProps}
      >
        {childIsText ? (
          <Text textStyle="base-emphasized">{children}</Text>
        ) : (
          children
        )}
      </Flex>
    );
  },
);

(Title as typeof Title & { displayName: string }).displayName =
  'MessageContainer.Title';
